import type { WidgetDefinition } from '../WidgetDefinition';
import { faSolarSystem } from '@fortawesome/pro-regular-svg-icons/faSolarSystem';
import { generatorRoutine } from '@thinkalpha/ui-core/routine/index';
import type { ScreenerWidgetModel } from '@thinkalpha/ui-types/contracts/workspace';
import { createDeferredIdeaHydration, shouldDeferIdeaHydration } from 'src/lib/widget/deferredIdeaHydration';
import { createDefaultScreenerWidget } from 'src/store/defaults/widget';
import type { ScreenerWidgetViewModel } from 'src/store/types';

class ScreenerWidgetDefinition implements WidgetDefinition<ScreenerWidgetViewModel, ScreenerWidgetModel> {
    type = 'screener' as const;
    nameKey = 'widgets::screener::name' as const;
    roles = ['widgets::screener'];
    icon = faSolarSystem;
    category = 'widgets::categories::research' as const;
    defaultDimensions = { h: 0.7, w: 0.6, x: 0.2, y: 0.05 };

    /**
     * Converts a Screener widget from contract model to view model.
     *
     * Handles three serialized forms of `idea`:
     *  - string id: clean saved idea, deferred to render-time hydration
     *  - { id, ... }: dirty draft of a saved idea, deferred to render-time hydration
     *  - { ... } without id: new unsaved idea
     */
    toViewModel = generatorRoutine(function* (_ctx, widget: ScreenerWidgetModel) {
        const { idea: incomingIdea, ...restWidget } = widget;

        let idea: ScreenerWidgetViewModel['idea'] | undefined;
        let isIdeaDirty = false;
        const ideaHydration = shouldDeferIdeaHydration(incomingIdea)
            ? createDeferredIdeaHydration(incomingIdea)
            : undefined;

        if (!ideaHydration && incomingIdea && typeof incomingIdea !== 'string') {
            idea = incomingIdea;
            isIdeaDirty = true;
        }

        return createDefaultScreenerWidget({
            ...restWidget,
            idea,
            isIdeaDirty,
            ...(ideaHydration ? { ideaHydration } : {}),
        });
    });

    /**
     * Converts a Screener widget from view model to contract model
     */
    toContractModel(widget: ScreenerWidgetViewModel): ScreenerWidgetModel {
        const { idea, ideaHydration, isIdeaDirty, ...restWidget } = widget;

        return {
            ...restWidget,
            idea:
                ideaHydration && ideaHydration.status !== 'ready'
                    ? ideaHydration.source
                    : idea?.id && isIdeaDirty === false
                      ? idea.id
                      : idea,
            type: 'screener',
        };
    }
}

export const screenerWidgetDefinition = new ScreenerWidgetDefinition();
